<script setup lang="ts">
import BaseButton from '@components/quiz_v2/BaseButton/BaseButton.vue';
import { Period } from '@models/checkout';
import { StepCheckout } from '@models/quiz';
import { useCheckoutStore } from '@stores/checkout/checkoutStore';
import { useQuizStore } from '@stores/quiz';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import IconArrowLeft from '../../assets/icons/arrow-left.svg';
import IconArrowRight from '../../assets/icons/arrow-right.svg';
import IconCheck from '../../assets/icons/check.svg';
// import CheckoutCartAgreement from '../CheckoutCartAgreement/CheckoutCartAgreement.vue';
// import CheckoutCartCoupon from '../CheckoutCartCoupon/CheckoutCartCoupon.vue';
import { useWeightLossPlans } from '../../composables/useWeightLossPlans';
import CheckoutCartTotal from '../CheckoutCartTotal/CheckoutCartTotal.vue';
import CheckoutProductCardSmall from '../CheckoutProductCardSmall/CheckoutProductCardSmall.vue';
import PeriodItem from '../PeriodItem/PeriodItem.vue';

const props = withDefaults(defineProps<{
  content: StepCheckout;
  name?: string;
}>(), {
  name: '',
});

const emit = defineEmits<{
  'next': [];
  'back': [];
}>();

const checkoutStore = useCheckoutStore();
const quizStore = useQuizStore();
const { periodOptions, activePeriodOption } = useWeightLossPlans();

const {
  variantForm,
  variant$,
  cartItems,
  mainProduct,
  membershipProduct,
  estimateLoading,
} = storeToRefs(checkoutStore);

const { scrollToError } = checkoutStore;

const setPeriod = (period: Period) => {
  variantForm.value.period.value = period.value;
  variantForm.value.period.unit = period.unit;
};

// Set recommended period
const recommendedPeriod = periodOptions.value.find((option) => option.recommended);

if (recommendedPeriod) {
  setPeriod(recommendedPeriod);
}

const isVariantSelected = computed(() => cartItems.value.length > +!!membershipProduct.value);

const next = async () => {
  if (!await variant$.value.$validate()) {
    scrollToError();
    return;
  }

  emit('next');
};

const subtitle = computed(() => {
  return props.name
    ? `${props.name}’s treatment details`
    : 'Treatment details';
});

const currentWeight = computed(() => {
  const value = quizStore.answerArray
    .find(({ id }) => id === props.content.currentWeight[0])
    ?.value || 0;

  return Number(value);
});

const loseWeight = computed(() => {
  const finalWeight = quizStore.answerArray
    .find(({ id }) => id === props.content.goal[0])
    ?.value || 0;

  return +(currentWeight.value - Number(finalWeight)).toFixed(2);
});
</script>

<template>
  <div class="flex flex-col items-center">
    <div class="checkout-slide-left w-full max-w-[530px]">
      <div class="-mx-20">
        <h1 class="px-24 text-28 font-medium text-primary-400">
          You’re almost done
        </h1>

        <div class="mt-32 rounded-[10px] bg-white px-20 py-30">
          <header>
            <h2 class="text-18 font-bold">
              {{ subtitle }}
            </h2>
            <p
              v-if="loseWeight > 0"
              class="mt-4 text-24 text-primary-500"
            >
              Based on your goal to lose {{ loseWeight }} pounds
            </p>
          </header>

          <CheckoutProductCardSmall
            v-if="mainProduct"
            class="mt-30"
            :product="mainProduct"
          />

          <div
            class="mt-36"
            :class="{ 'validation-error': variant$.period.$error }"
          >
            <ul class="flex flex-col gap-16">
              <li
                v-for="period in periodOptions"
                :key="period.id"
              >
                <PeriodItem
                  :title="period.title"
                  :price="period.formattedPrice"
                  :month-price="period.monthPrice"
                  :off-price="period.offPrice"
                  :label="period.label"
                  :is-open="period.value === variantForm.period.value && period.unit === variantForm.period.unit"
                  :loading="estimateLoading"
                  @click="setPeriod(period)"
                >
                  <p class="text-14 font-bold text-primary-500">
                    Plan Breakdown
                  </p>

                  <ul class="mt-16 flex list-inside list-disc flex-col gap-10 pl-10 text-14">
                    <li
                      v-for="(text, index) in period.list"
                      :key="index"
                      :class="{
                        'text-primary-500': index === 1,
                      }"
                    >
                      {{ text }}
                    </li>
                  </ul>

                  <div class="mt-28 rounded-[5px] bg-primary-50 p-20 text-center text-12 text-black">
                    <p class="mx-auto max-w-[248px]">
                      Cancel or change your plan anytime from your online account portal or the app
                    </p>
                  </div>

                  <div class="mt-28 flex items-start justify-between gap-16">
                    <p class="inline-block text-12">
                      FSA/HSA eligible for reimbursement
                    </p>

                    <div class="flex items-center gap-10 text-12">
                      <IconCheck class="size-20" />
                      <span>Selected</span>
                    </div>
                  </div>
                </PeriodItem>
              </li>
            </ul>

            <p
              v-if="variant$.period.$error"
              class="mt-8 text-14 text-error-500"
            >
              {{ variant$.period.$errors[0]?.$message }}
            </p>
          </div>

          <template v-if="isVariantSelected">
            <!-- <CheckoutCartCoupon class="mt-36" /> -->
            <!-- <CheckoutCartAgreement class="mt-36" /> -->

            <div class="mt-36 border-t border-primary-200 pt-32">
              <CheckoutCartTotal />
            </div>
          </template>
        </div>
      </div>

      <BaseButton
        class="sticky bottom-16 mt-40 w-full md:mt-60"
        :color="!isVariantSelected ? 'primary' : 'black'"
        data-testpl="checkout-plans-continue-btn"
        animated
        @click="next"
      >
        Continue with {{ mainProduct?.product.title }}

        <template #icon-right>
          <IconArrowRight />
        </template>
      </BaseButton>

      <BaseButton
        class="mt-16 w-full"
        theme="outlined"
        data-testpl="checkout-plans-back-btn"
        animated
        @click="emit('back')"
      >
        Back

        <template #icon-left>
          <IconArrowLeft />
        </template>
      </BaseButton>

      <div
        v-if="activePeriodOption?.agreements"
        class="mt-60 flex flex-col gap-10 text-center text-12 text-black opacity-50"
        v-html="activePeriodOption.agreements"
      />
    </div>
  </div>
</template>
